import React, { useEffect, useState } from "react";
import { C } from "../constants.js";
import { I } from "../icons.jsx";
import { apiFetch, invalidateApiCache } from "../api.js";
import { Spinner } from "../components/Spinner.jsx";

export function NewMatterPage({ onMatterClick, showNotification }) {
  const [step, setStep] = useState("client");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [options, setOptions] = useState(null);
  const [loadingOptions, setLoadingOptions] = useState(true);

  const [createdContact, setCreatedContact] = useState(null);
  const [createdMatter, setCreatedMatter] = useState(null);

  const [clientForm, setClientForm] = useState({
    first_name: "", last_name: "", date_of_birth: "",
    phone: "", email: "", street: "", city: "", state: "", zip_code: ""
  });
  const [matterForm, setMatterForm] = useState({
    description: "", responsible_attorney_id: "", practice_area_id: ""
  });
  const [customFieldsForm, setCustomFieldsForm] = useState({
    date_of_incident: "", claim_number: ""
  });
  const [insuranceForm, setInsuranceForm] = useState({
    company_name: "", adjuster_first: "", adjuster_last: "",
    adjuster_phone: "", adjuster_email: ""
  });

  useEffect(() => {
    apiFetch("/api/new-matter/options")
      .then(data => { setOptions(data); setLoadingOptions(false); })
      .catch(err => { setError(err.message); setLoadingOptions(false); });
  }, []);

  // Form field helper
  const field = (label, key, formState, setFormState, opts = {}) =>
    React.createElement("div", { style: { marginBottom:"14px", flex: opts.flex || "1" } },
      React.createElement("label", {
        style: { fontSize:"11px", color:C.textMuted, fontWeight:600, display:"block", marginBottom:"6px", textTransform:"uppercase", letterSpacing:"0.04em" }
      }, label, opts.required && React.createElement("span", { style:{ color:C.error } }, " *")),
      React.createElement("input", {
        type: opts.type || "text",
        value: formState[key],
        onChange: e => setFormState(prev => ({ ...prev, [key]: e.target.value })),
        placeholder: opts.placeholder || "",
        style: {
          width:"100%", padding:"10px 14px", borderRadius:"8px",
          border:`1px solid ${C.warmGray}`, fontSize:"14px",
          color:C.textDark, background:"#fff",
        }
      })
    );

  // Select field helper
  const selectField = (label, key, formState, setFormState, selectOptions, opts = {}) =>
    React.createElement("div", { style: { marginBottom:"14px", flex: opts.flex || "1" } },
      React.createElement("label", {
        style: { fontSize:"11px", color:C.textMuted, fontWeight:600, display:"block", marginBottom:"6px", textTransform:"uppercase", letterSpacing:"0.04em" }
      }, label),
      React.createElement("select", {
        value: formState[key],
        onChange: e => setFormState(prev => ({ ...prev, [key]: e.target.value })),
        style: {
          width:"100%", padding:"10px 14px", borderRadius:"8px",
          border:`1px solid ${C.warmGray}`, fontSize:"14px",
          color: formState[key] ? C.textDark : C.textLight, background:"#fff", cursor:"pointer",
        }
      },
        React.createElement("option", { value:"" }, opts.placeholder || "Select..."),
        (selectOptions || []).map(o => React.createElement("option", { key:o.id, value:o.id }, o.name))
      )
    );

  const steps = [
    { id: "client", label: "Client" },
    { id: "matter", label: "Matter" },
    { id: "custom_fields", label: "Case Details" },
    { id: "relationships", label: "Relationships" },
  ];
  const stepIndex = steps.findIndex(s => s.id === step);
  const isDone = step === "done";

  // Step indicator
  const stepIndicator = React.createElement("div", {
    style: { display:"flex", alignItems:"center", justifyContent:"center", marginBottom:"28px", gap:"0" }
  },
    steps.map((s, i) => {
      const isComplete = isDone || i < stepIndex;
      const isCurrent = !isDone && i === stepIndex;
      return React.createElement(React.Fragment, { key: s.id },
        i > 0 && React.createElement("div", {
          style: { width:"48px", height:"2px", background: isComplete ? C.gold : C.warmGray }
        }),
        React.createElement("div", {
          style: { display:"flex", flexDirection:"column", alignItems:"center", gap:"6px" }
        },
          React.createElement("div", {
            style: {
              width:32, height:32, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:"13px", fontWeight:600,
              background: isComplete ? C.gold : isCurrent ? "#fff" : C.warmWhite,
              color: isComplete ? "#fff" : isCurrent ? C.gold : C.textLight,
              border: isCurrent ? `2px solid ${C.gold}` : isComplete ? "none" : `1px solid ${C.warmGray}`,
            }
          }, isComplete ? I.check : i + 1),
          React.createElement("div", {
            style: { fontSize:"11px", fontWeight: isCurrent ? 600 : 400, color: isCurrent ? C.navy : C.textMuted, whiteSpace:"nowrap" }
          }, s.label),
        ),
      );
    })
  );

  // Button bar helper
  const buttonBar = (onSave, saveLabel, opts = {}) =>
    React.createElement("div", {
      style: { display:"flex", justifyContent:"flex-end", gap:"10px", marginTop:"20px", paddingTop:"20px", borderTop:`1px solid ${C.warmGray}60` }
    },
      opts.onSkip && React.createElement("button", {
        onClick: opts.onSkip,
        style: {
          padding:"12px 24px", borderRadius:"10px", border:`1px solid ${C.warmGray}`,
          background:"#fff", color:C.textMuted, fontSize:"14px", fontWeight:600, cursor:"pointer",
        }
      }, "Skip"),
      React.createElement("button", {
        onClick: onSave,
        disabled: saving || opts.disabled,
        style: {
          padding:"12px 24px", borderRadius:"10px", border:"none",
          background: saving || opts.disabled ? C.warmGray : C.navy,
          color: saving || opts.disabled ? C.textLight : "#fff",
          fontSize:"14px", fontWeight:600, cursor: saving || opts.disabled ? "default" : "pointer",
          display:"flex", alignItems:"center", gap:"8px",
        }
      },
        saving ? React.createElement(React.Fragment, null, React.createElement(Spinner, {size:16,color:"#fff"}), " Saving...") : saveLabel,
      ),
    );

  // --- Step handlers ---
  const handleSaveClient = async () => {
    if (!clientForm.first_name || !clientForm.last_name) {
      setError("First name and last name are required.");
      return;
    }
    setSaving(true); setError(null);
    try {
      const res = await apiFetch("/api/new-matter/contact", {
        method: "POST", body: JSON.stringify(clientForm),
      });
      setCreatedContact(res.data);
      setMatterForm(prev => ({
        ...prev,
        description: `${clientForm.last_name}, ${clientForm.first_name}`,
      }));
      setStep("matter");
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  const handleSaveMatter = async () => {
    if (!matterForm.description) {
      setError("Matter description is required.");
      return;
    }
    setSaving(true); setError(null);
    try {
      const res = await apiFetch("/api/new-matter/matter", {
        method: "POST",
        body: JSON.stringify({
          client_id: createdContact.id,
          description: matterForm.description,
          responsible_attorney_id: matterForm.responsible_attorney_id ? Number(matterForm.responsible_attorney_id) : undefined,
          practice_area_id: matterForm.practice_area_id ? Number(matterForm.practice_area_id) : undefined,
        }),
      });
      // New matter created — bust any cached /api/matters lists so the
      // dashboard and matters page show the new row on next mount.
      invalidateApiCache("/api/matters");
      setCreatedMatter(res.data);
      setStep("custom_fields");
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  const handleSaveCustomFields = async () => {
    if (!customFieldsForm.date_of_incident && !customFieldsForm.claim_number) {
      setStep("relationships");
      return;
    }
    setSaving(true); setError(null);
    try {
      const res = await apiFetch("/api/new-matter/custom-fields", {
        method: "POST",
        body: JSON.stringify({
          matter_id: createdMatter.id,
          date_of_incident: customFieldsForm.date_of_incident || undefined,
          claim_number: customFieldsForm.claim_number || undefined,
        }),
      });
      if (res.warnings && res.warnings.length > 0) {
        showNotification(res.warnings.join("; "), "error");
      }
      setStep("relationships");
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  const handleSaveRelationships = async () => {
    setSaving(true); setError(null);
    try {
      // Insurance company
      if (insuranceForm.company_name) {
        await apiFetch("/api/new-matter/relationship", {
          method: "POST",
          body: JSON.stringify({
            matter_id: createdMatter.id,
            description: "Insurance Company",
            contact_type: "Company",
            contact_name: insuranceForm.company_name,
          }),
        });
      }
      // Adjuster
      if (insuranceForm.adjuster_first || insuranceForm.adjuster_last) {
        await apiFetch("/api/new-matter/relationship", {
          method: "POST",
          body: JSON.stringify({
            matter_id: createdMatter.id,
            description: "Adjuster",
            contact_type: "Person",
            first_name: insuranceForm.adjuster_first,
            last_name: insuranceForm.adjuster_last,
            phone: insuranceForm.adjuster_phone || undefined,
            email: insuranceForm.adjuster_email || undefined,
          }),
        });
      }
      setStep("done");
      showNotification("Matter created successfully in Clio");
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  const handleReset = () => {
    setStep("client");
    setCreatedContact(null);
    setCreatedMatter(null);
    setClientForm({ first_name:"", last_name:"", date_of_birth:"", phone:"", email:"", street:"", city:"", state:"", zip_code:"" });
    setMatterForm({ description:"", responsible_attorney_id:"", practice_area_id:"" });
    setCustomFieldsForm({ date_of_incident:"", claim_number:"" });
    setInsuranceForm({ company_name:"", adjuster_first:"", adjuster_last:"", adjuster_phone:"", adjuster_email:"" });
    setError(null);
  };

  if (loadingOptions) {
    return React.createElement("div", {
      style: { padding:"32px 40px", display:"flex", justifyContent:"center", paddingTop:"120px" }
    }, React.createElement(Spinner, { size: 32 }));
  }

  return React.createElement("div", { className:"fade-in", style: { padding:"32px 40px", maxWidth:"720px" } },
    React.createElement("h1", {
      style: { fontFamily:"'Playfair Display',serif", fontSize:"28px", fontWeight:600, color:C.navy, marginBottom:"6px" }
    }, "New Matter"),
    React.createElement("p", {
      style: { fontSize:"15px", color:C.textMuted, marginBottom:"28px" }
    }, "Create a new client and matter in Clio."),

    stepIndicator,

    // Error banner
    error && React.createElement("div", {
      style: { padding:"12px 16px", borderRadius:"10px", background:C.errorBg, color:C.error, fontSize:"13px", marginBottom:"16px", display:"flex", alignItems:"center", gap:"8px" }
    }, I.alert, error),

    // --- Step: Client ---
    step === "client" && React.createElement("div", {
      style: { background:"#fff", borderRadius:"12px", padding:"24px 28px", border:`1px solid ${C.warmGray}` }
    },
      React.createElement("h2", {
        style: { fontSize:"13px", color:C.textMuted, textTransform:"uppercase", letterSpacing:"0.08em", fontWeight:600, marginBottom:"18px" }
      }, "Client Information"),
      React.createElement("div", { style: { display:"flex", gap:"14px" } },
        field("First Name", "first_name", clientForm, setClientForm, { required: true, placeholder:"John" }),
        field("Last Name", "last_name", clientForm, setClientForm, { required: true, placeholder:"Doe" }),
      ),
      React.createElement("div", { style: { display:"flex", gap:"14px" } },
        field("Date of Birth", "date_of_birth", clientForm, setClientForm, { type:"date" }),
        field("Phone", "phone", clientForm, setClientForm, { placeholder:"(251) 555-0100" }),
      ),
      field("Email", "email", clientForm, setClientForm, { placeholder:"john@example.com" }),
      React.createElement("div", {
        style: { fontSize:"11px", color:C.textMuted, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.04em", marginBottom:"10px", marginTop:"8px" }
      }, "Address"),
      field("Street", "street", clientForm, setClientForm, { placeholder:"123 Main St" }),
      React.createElement("div", { style: { display:"flex", gap:"14px" } },
        field("City", "city", clientForm, setClientForm, { placeholder:"Mobile" }),
        field("State", "state", clientForm, setClientForm, { placeholder:"AL" }),
        field("ZIP", "zip_code", clientForm, setClientForm, { placeholder:"36602" }),
      ),
      buttonBar(handleSaveClient, "Save & Continue", {
        disabled: !clientForm.first_name || !clientForm.last_name,
      }),
    ),

    // --- Step: Matter ---
    step === "matter" && React.createElement("div", {
      style: { background:"#fff", borderRadius:"12px", padding:"24px 28px", border:`1px solid ${C.warmGray}` }
    },
      React.createElement("h2", {
        style: { fontSize:"13px", color:C.textMuted, textTransform:"uppercase", letterSpacing:"0.08em", fontWeight:600, marginBottom:"18px" }
      }, "Matter Details"),
      React.createElement("div", {
        style: { padding:"10px 14px", borderRadius:"8px", background:`${C.gold}08`, border:`1px solid ${C.gold}30`, marginBottom:"16px", fontSize:"13px", color:C.navy }
      },
        "Client: ", React.createElement("strong", null, createdContact?.name || `${clientForm.first_name} ${clientForm.last_name}`),
      ),
      field("Description", "description", matterForm, setMatterForm, { required: true, placeholder:"Doe, John - Auto Accident" }),
      selectField("Responsible Attorney", "responsible_attorney_id", matterForm, setMatterForm,
        (options?.attorneys || []), { placeholder:"Select attorney..." }
      ),
      selectField("Practice Area", "practice_area_id", matterForm, setMatterForm,
        (options?.practice_areas || []), { placeholder:"Select practice area..." }
      ),
      buttonBar(handleSaveMatter, "Save & Continue", {
        disabled: !matterForm.description,
      }),
    ),

    // --- Step: Custom Fields ---
    step === "custom_fields" && React.createElement("div", {
      style: { background:"#fff", borderRadius:"12px", padding:"24px 28px", border:`1px solid ${C.warmGray}` }
    },
      React.createElement("h2", {
        style: { fontSize:"13px", color:C.textMuted, textTransform:"uppercase", letterSpacing:"0.08em", fontWeight:600, marginBottom:"4px" }
      }, "Case Details"),
      React.createElement("p", {
        style: { fontSize:"13px", color:C.textLight, marginBottom:"18px" }
      }, "Optional \u2014 these fields are used by document templates."),
      React.createElement("div", {
        style: { padding:"10px 14px", borderRadius:"8px", background:`${C.gold}08`, border:`1px solid ${C.gold}30`, marginBottom:"16px", fontSize:"13px", color:C.navy }
      },
        "Matter: ", React.createElement("strong", null, createdMatter?.display_number || matterForm.description),
      ),
      field("Date of Incident", "date_of_incident", customFieldsForm, setCustomFieldsForm, { type:"date" }),
      field("Claim Number", "claim_number", customFieldsForm, setCustomFieldsForm, { placeholder:"CLM-2026-001" }),
      buttonBar(handleSaveCustomFields, "Save & Continue", {
        onSkip: () => setStep("relationships"),
      }),
    ),

    // --- Step: Relationships ---
    step === "relationships" && React.createElement("div", {
      style: { background:"#fff", borderRadius:"12px", padding:"24px 28px", border:`1px solid ${C.warmGray}` }
    },
      React.createElement("h2", {
        style: { fontSize:"13px", color:C.textMuted, textTransform:"uppercase", letterSpacing:"0.08em", fontWeight:600, marginBottom:"4px" }
      }, "Relationships"),
      React.createElement("p", {
        style: { fontSize:"13px", color:C.textLight, marginBottom:"18px" }
      }, "Optional \u2014 add insurance and adjuster information."),

      // Insurance Company
      React.createElement("div", {
        style: { fontSize:"12px", color:C.navy, fontWeight:600, marginBottom:"10px", marginTop:"4px" }
      }, "Insurance Company"),
      field("Company Name", "company_name", insuranceForm, setInsuranceForm, { placeholder:"State Farm Insurance" }),

      // Adjuster
      React.createElement("div", {
        style: { fontSize:"12px", color:C.navy, fontWeight:600, marginBottom:"10px", marginTop:"8px" }
      }, "Adjuster"),
      React.createElement("div", { style: { display:"flex", gap:"14px" } },
        field("First Name", "adjuster_first", insuranceForm, setInsuranceForm, { placeholder:"Jane" }),
        field("Last Name", "adjuster_last", insuranceForm, setInsuranceForm, { placeholder:"Smith" }),
      ),
      React.createElement("div", { style: { display:"flex", gap:"14px" } },
        field("Phone", "adjuster_phone", insuranceForm, setInsuranceForm, { placeholder:"(555) 123-4567" }),
        field("Email", "adjuster_email", insuranceForm, setInsuranceForm, { placeholder:"adjuster@insurance.com" }),
      ),
      buttonBar(handleSaveRelationships, "Save & Finish", {
        onSkip: () => { setStep("done"); showNotification("Matter created successfully in Clio"); },
      }),
    ),

    // --- Done ---
    step === "done" && React.createElement("div", {
      style: { background:"#fff", borderRadius:"12px", padding:"40px 28px", border:`1px solid ${C.warmGray}`, textAlign:"center" }
    },
      React.createElement("div", {
        style: { width:56, height:56, borderRadius:"50%", background:C.successBg, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 16px", color:C.success }
      }, React.createElement("svg", {width:28,height:28,viewBox:"0 0 24 24",fill:"none",stroke:"currentColor",strokeWidth:2.5,strokeLinecap:"round"},
        React.createElement("polyline",{points:"20 6 9 17 4 12"})
      )),
      React.createElement("h2", {
        style: { fontFamily:"'Playfair Display',serif", fontSize:"22px", fontWeight:600, color:C.navy, marginBottom:"8px" }
      }, "Matter Created"),
      React.createElement("p", {
        style: { fontSize:"14px", color:C.textMuted, marginBottom:"24px" }
      },
        `${createdContact?.name || clientForm.first_name + " " + clientForm.last_name}`,
        createdMatter?.display_number && ` \u2014 ${createdMatter.display_number}`,
      ),

      // Summary
      React.createElement("div", {
        style: { display:"inline-flex", flexDirection:"column", gap:"6px", textAlign:"left", marginBottom:"28px" }
      },
        [
          { label: "Contact", value: createdContact?.name, done: true },
          { label: "Matter", value: createdMatter?.display_number || createdMatter?.description, done: true },
          { label: "Case Details", value: customFieldsForm.date_of_incident ? "Set" : "Skipped", done: !!customFieldsForm.date_of_incident },
          { label: "Insurance", value: insuranceForm.company_name || "Skipped", done: !!insuranceForm.company_name },
        ].map((item, i) =>
          React.createElement("div", {
            key: i,
            style: { display:"flex", alignItems:"center", gap:"10px", fontSize:"13px" }
          },
            React.createElement("span", { style: { color: item.done ? C.success : C.textLight } }, item.done ? I.check : "\u2014"),
            React.createElement("span", { style: { color:C.textMuted, width:"90px" } }, item.label),
            React.createElement("span", { style: { color:C.navy, fontWeight:500 } }, item.value),
          )
        ),
      ),

      // The Clio document structure is built in the background from
      // here — ~85 calls over a minute. Said plainly rather than left
      // to be discovered, because somebody who opens Clio in the next
      // thirty seconds will see a half-built tree and wonder.
      React.createElement("p", {
        style: { textAlign: "center", fontSize: "12px", color: C.textMuted,
                 margin: "0 0 18px" },
      }, "Document folders are being created in Clio now. This takes about "
       + "a minute, and the matter is ready to use before it finishes."),

      // Action buttons
      React.createElement("div", { style: { display:"flex", justifyContent:"center", gap:"12px", flexWrap:"wrap" } },
        React.createElement("button", {
          onClick: handleReset,
          style: {
            padding:"12px 24px", borderRadius:"10px", border:`1px solid ${C.warmGray}`,
            background:"#fff", color:C.textMuted, fontSize:"14px", fontWeight:600, cursor:"pointer",
          }
        }, "Create Another"),
        React.createElement("button", {
          onClick: () => onMatterClick({ id: createdMatter?.id, client: { name: createdContact?.name }, display_number: createdMatter?.display_number, status: "Open" }),
          style: {
            padding:"12px 24px", borderRadius:"10px", border:"none",
            background:C.navy, color:"#fff", fontSize:"14px", fontWeight:600, cursor:"pointer",
            display:"flex", alignItems:"center", gap:"8px",
          }
        }, "View Matter ", I.chevron),
      ),
    ),
  );
}

